import { action, computed, observable } from "mobx";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import JobStatus from "~/constants/PhotoEditing/JobStatus";
import JobType from "~/constants/PhotoEditing/JobType";
import PhotoEditingJobRepository from "~/repository/PhotoEditing/PhotoEditingJobRepository";

class HomeStore {
  /**
   * @property {Array}
   */
  @observable photoEditingJobs = [];

  /**
   * @property {boolean} hasPhotoEditingJobs the tour has jobs at all - regardless if the jobs have been filtered by a
   *                     search action.
   */
  @observable hasPhotoEditingJobs = false;

  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {string} query the search query
   */
  @observable query = "";

  /**
   * @property {string} query the status filter
   */
  @observable status = "";

  /**
   * @property {Object}
   */
  @observable pagination = {};

  @observable tourId = null;

  photoEditingJobRepository = null;

  constructor({ tourId = null }) {
    this.tourId = tourId;

    this.pagination = {
      currentPage: 1,
      itemsPerPage: 10,
      total: 0,
      showingFrom: 0,
      showingTo: 0,
      totalPages: 0,
    };

    this.photoEditingJobRepository = new PhotoEditingJobRepository();
  }

  /**
   * Fetch jobs and tell if tour has jobs at all.
   */
  @action
  load() {
    this.isLoading = true;

    return this.fetchPhotoEditingJobs()
      .then(() => {
        this.hasPhotoEditingJobs = this.photoEditingJobs.length > 0;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   * For internal use only.
   */
  @action
  fetchPhotoEditingJobs() {
    this.isLoading = true;

    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.query.trim() !== "") {
      params.photoEditingJobIdOrLabel = this.query.trim();
    }

    if (this.status !== "") {
      params.status = this.status;
    }

    if (this.tourId) {
      params.tourId = this.tourId;
    }

    return this.photoEditingJobRepository
      .fetch(params)
      .then(response => {
        this.processIncomingJobs(response.data.photoEditingJobs);
        this.processPagination(response.data.pagination);
      })
      .finally(() => {
        this.isLoading = false;
        // trim the query if it's filled of blank spaces only (aesthetic feature)
        if (this.query.trim() === "") {
          this.query = "";
        }
      });
  }

  /**
   * Reset current page whenever a search is made and fetch jobs.
   */
  @action
  searchPhotoEditingJobs() {
    this.pagination.currentPage = 1;

    return this.fetchPhotoEditingJobs();
  }

  @action
  processIncomingJobs(incomingJobs = []) {
    this.photoEditingJobs = [];

    incomingJobs.forEach(incomingJob => {
      const photoEditingJob = new PhotoEditingJob(incomingJob);
      // might be badly performant once there are many jobs from the backend
      photoEditingJob.tickEstimatedTimeLeftToFinish();
      this.photoEditingJobs.push(photoEditingJob);
    });
  }

  /**
   * Get a list of status options consumable by a Dropdown component.
   */
  static getStatusOptions() {
    return [
      { key: "any", value: "", text: "Any" },
      { key: JobStatus.IN_CREATION, value: JobStatus.IN_CREATION, text: "In Creation" },
      { key: JobStatus.IN_PROGRESS, value: JobStatus.IN_PROGRESS, text: "In Progress" },
      { key: JobStatus.DONE, value: JobStatus.DONE, text: "Done" },
    ];
  }

  @action
  onQueryChange(query) {
    this.query = query;
  }

  @action
  onStatusChange(status) {
    this.status = status;
  }

  @action
  deletePhotoEditingJob(photoEditingJobId) {
    this.isLoading = true;

    return this.photoEditingJobRepository
      .delete(photoEditingJobId)
      .then(this.fetchPhotoEditingJobs())
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   * Change the pagination page and fetch the jobs.
   */
  @action
  changePage(page) {
    this.pagination.currentPage = page;

    return this.fetchPhotoEditingJobs();
  }

  @action
  processPagination(pagination) {
    const { offset, recordsFiltered, recordsTotal } = pagination;

    this.pagination.total = 0;
    this.pagination.showingFrom = 0;
    this.pagination.showingTo = 0;
    this.pagination.totalPages = 0;

    if (recordsFiltered > 0) {
      this.pagination.total = recordsTotal;
      this.pagination.showingFrom = offset + 1;
      this.pagination.showingTo = offset + recordsFiltered;
      this.pagination.totalPages = Math.ceil(recordsTotal / this.pagination.itemsPerPage);
    }
  }

  @computed
  get offset() {
    return (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
  }

  @action
  createBasicHdrPhotoEditingJob() {
    this.isLoading = true;

    return this.photoEditingJobRepository.create(this.tourId, JobType.BASIC_HDR).finally(() => {
      this.isLoading = false;
    });
  }
}

export default HomeStore;
